import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiPrinter, FiCheckSquare, FiSquare } from 'react-icons/fi';
import { useProjects } from '../context/ProjectContext';
import AutoTextarea from './AutoTextarea';
import { downloadPdfReport } from '../utils/printReport';
import { get as idbGet, set as idbSet } from '../utils/idbKV';

const Checkbox = ({ checked, onChange, label, style = {} }) => (
    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', ...style }}>
        {checked ? <FiCheckSquare /> : <FiSquare />}
        <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            style={{ display: 'none' }}
        />
        <span style={{ marginLeft: 6 }}>{label}</span>
    </label>
);

function getVarmetabPdfData(project) {
    if (!project) return null;

    const raw =
        project?.varmetabPayload ??
        project?.varmetab?.payload ??
        project?.varmetab?.state ??
        project?.varmetab?.result ??
        project?.varmetab?.data ??
        project?.varmetab ??
        null;

    if (!raw) return null;

    const payload =
        raw?.frames?.ramme ??
        raw?.ramme ??
        raw?.payload ??
        raw?.state ??
        raw?.result ??
        raw?.data ??
        raw;

    if (!payload || typeof payload !== 'object') return null;

    const hasRows = [
        'existPlan',
        'existLine',
        'coverPlan',
        'coverLine',
        'designPlan',
        'designLine',
    ].some((key) => Array.isArray(payload?.[key]) && payload[key].length > 0);

    if (!hasRows) return null;

    return {
        ...payload,
        info: project?.varmetabInfo ?? payload?.info ?? raw?.info ?? {},
    };
}

export default function PrintModal({ open, onClose }) {
    const { activeProject, setReportNote } = useProjects();

    const [author, setAuthor] = useState('');
    const [note, setNote] = useState(() => activeProject?.reportNote || '');

    const [incUvalue, setIncUvalue] = useState(true);
    const [incVarmetab, setIncVarmetab] = useState(true);
    const [incSavings, setIncSavings] = useState(true);

    const constructions = activeProject?.uvalue ?? [];
    const [uvAll, setUvAll] = useState(true);
    const [uvSel, setUvSel] = useState(() => new Set(constructions.map((c) => c.id)));

    const savedSingles = activeProject?.savings?.singleTests ?? [];
    const savedBuildings = activeProject?.savings?.buildingTests ?? [];
    const scenList = [...savedSingles, ...savedBuildings];

    const [svAll, setSvAll] = useState(true);
    const [svSel, setSvSel] = useState(() => new Set(scenList.map((s) => s.id)));

    const boxRef = useRef(null);

    useEffect(() => {
        let alive = true;

        (async () => {
            const v = await idbGet('energiberegner:author');
            if (alive && typeof v === 'string') setAuthor(v);
        })();

        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        setNote(activeProject?.reportNote || '');
        setUvSel(new Set((activeProject?.uvalue ?? []).map((c) => c.id)));

        const singles = activeProject?.savings?.singleTests ?? [];
        const buildings = activeProject?.savings?.buildingTests ?? [];
        setSvSel(new Set([...singles, ...buildings].map((s) => s.id)));
    }, [activeProject?.name]);

    useEffect(() => {
        if (!open) return;

        const handleMouseDown = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) onClose();
        };

        document.addEventListener('mousedown', handleMouseDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, [open, onClose]);

    const handleDownload = async () => {
        const uDeck = incUvalue
            ? (uvAll ? constructions : constructions.filter((c) => uvSel.has(c.id)))
            : [];

        const vtData = incVarmetab ? getVarmetabPdfData(activeProject) : null;

        let savingsData = null;

        if (incSavings && scenList.length) {
            const chosen = svAll ? scenList : scenList.filter((s) => svSel.has(s.id));
            savingsData = chosen.length === 1 ? chosen[0] : chosen;
        }

        await downloadPdfReport({
            projectName: activeProject?.name,
            uvalueData: uDeck,
            varmetabData: vtData,
            savingsData,
            author: author.trim(),
            reportNote: note.trim(),
        });

        await idbSet('energiberegner:author', author.trim());

        if (activeProject?.name) {
            setReportNote(activeProject.name, note.trim());
        }

        onClose();
    };

    if (typeof window === 'undefined' || !open) return null;

    return createPortal(
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 5010,
            }}
        >
            <div
                ref={boxRef}
                style={{
                    background: '#fff',
                    borderRadius: 8,
                    padding: '1.8rem 1.9rem 1.4rem',
                    width: 'min(440px,92%)',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.1rem',
                    boxShadow: '0 8px 22px rgba(0,0,0,.15)',
                }}
            >
                <h2 style={{ margin: 0 }}>Udskriv PDF-rapport</h2>

                <label style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                    <span>Udarbejdet af</span>
                    <input
                        className="bigSelect"
                        type="text"
                        placeholder="Navn / firma …"
                        value={author}
                        onChange={(e) => setAuthor(e.target.value)}
                    />
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                    <span>Beskrivelse (forside)</span>
                    <div style={{ display: 'flex', gap: '.5rem' }}>
                        <AutoTextarea
                            value={note}
                            onChange={setNote}
                            placeholder="Evt. ekstra bemærkninger …"
                            className="noteArea"
                        />

                        {note && (
                            <button
                                type="button"
                                className="iconBtn"
                                title="Ryd tekst"
                                onClick={() => setNote('')}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </label>

                <div style={{ borderTop: '1px solid #ddd', paddingTop: '1rem', marginTop: '-.3rem' }}>
                    <p style={{ margin: '0 0 .5rem', fontSize: 13, color: '#555' }}>
                        Klik for at slå rapportens afsnit til eller fra.
                    </p>

                    <Checkbox
                        checked={incUvalue}
                        onChange={setIncUvalue}
                        label="U-værdi"
                    />

                    {incUvalue && (
                        <div style={{ marginLeft: 18, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <Checkbox
                                checked={uvAll}
                                onChange={(v) => {
                                    setUvAll(v);
                                    if (v) setUvSel(new Set(constructions.map((c) => c.id)));
                                }}
                                label="Inkludér alle konstruktioner"
                            />

                            {!uvAll && constructions.map((c) => (
                                <Checkbox
                                    key={c.id}
                                    checked={uvSel.has(c.id)}
                                    onChange={(v) => {
                                        const next = new Set(uvSel);
                                        if (v) next.add(c.id);
                                        else next.delete(c.id);
                                        setUvSel(next);
                                    }}
                                    label={c.title || 'Konstruktion'}
                                    style={{ marginLeft: 18 }}
                                />
                            ))}
                        </div>
                    )}

                    <div style={{ marginTop: 10 }}>
                        <Checkbox
                            checked={incVarmetab}
                            onChange={setIncVarmetab}
                            label="Varmetabs-ramme"
                        />
                    </div>

                    <div style={{ marginTop: 10 }}>
                        <Checkbox
                            checked={incSavings}
                            onChange={setIncSavings}
                            label="Årlig besparelse"
                        />

                        {incSavings && scenList.length > 0 && (
                            <div style={{ marginLeft: 18, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <Checkbox
                                    checked={svAll}
                                    onChange={(v) => {
                                        setSvAll(v);
                                        if (v) setSvSel(new Set(scenList.map((s) => s.id)));
                                    }}
                                    label="Inkludér alle scenarier"
                                />

                                {!svAll && scenList.map((s) => (
                                    <Checkbox
                                        key={s.id}
                                        checked={svSel.has(s.id)}
                                        onChange={(v) => {
                                            const next = new Set(svSel);
                                            if (v) next.add(s.id);
                                            else next.delete(s.id);
                                            setSvSel(next);
                                        }}
                                        label={s.name || 'Scenario'}
                                        style={{ marginLeft: 18 }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                    <button
                        className="addBtn"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        onClick={handleDownload}
                    >
                        <FiPrinter /> Download PDF
                    </button>

                    <button onClick={onClose}>Annullér</button>
                </div>
            </div>
        </div>,
        document.body
    );
}